import { HubConnectionBuilder, LogLevel } from "@microsoft/signalr"
import { msalInstance, apiScopes } from "@/auth/msalConfig"
import { staffAccountKey } from "@/auth/staffAccount"
import { createRealtimeClient, type RealtimeStatus } from "@/services/realtime/realtimeClient"

declare const __API_BASE_URL__: string | undefined
const API_BASE_URL = typeof __API_BASE_URL__ !== "undefined" ? __API_BASE_URL__ : ""
const handlers = new Set<(type: string, payload: unknown) => void>()
const reconnectHandlers = new Set<() => void>()
const statusHandlers = new Set<(status: RealtimeStatus) => void>()
let lastStatus: RealtimeStatus = "stopped"

const client = createRealtimeClient({
  create() {
    const account = msalInstance.getActiveAccount()
    const identity = staffAccountKey()
    return new HubConnectionBuilder()
      .withUrl(`${API_BASE_URL}/hubs/notifications`, {
        accessTokenFactory: async () => {
          if (!account || !identity || identity !== staffAccountKey()) throw new Error("Staff session changed.")
          const result = await msalInstance.acquireTokenSilent({ ...apiScopes, account })
          if (identity !== staffAccountKey()) throw new Error("Staff session changed.")
          return result.accessToken
        },
      })
      .withAutomaticReconnect()
      // Avoid raw transport exceptions/URLs/tokens in application logging.
      .configureLogging(LogLevel.None)
      .build()
  },
  notification: (type, payload) => { for (const handler of handlers) handler(type, payload) },
  reconnected: () => { for (const handler of reconnectHandlers) handler() },
  status: value => { lastStatus = value; for (const handler of statusHandlers) handler(value) },
})

export const startConnection = (session: string) => client.start(session)
export const stopConnection = () => client.stop()
export const recoverConnection = () => client.recover()
// S3 transport seam. The benefit binding remains opt-in until R1; no second socket.
export const benefitSubscriptionTransport = {
  watchChannel: client.watchChannel,
  watchNotification: client.watchNotification,
}
export function onNotification(handler: (type: string, payload: unknown) => void): () => void {
  handlers.add(handler)
  return () => { handlers.delete(handler) }
}
export function onConnectionStatus(handler: (status: RealtimeStatus) => void): () => void {
  statusHandlers.add(handler)
  handler(lastStatus)
  return () => { statusHandlers.delete(handler) }
}
export function onReconnect(handler: () => void): () => void {
  reconnectHandlers.add(handler)
  return () => { reconnectHandlers.delete(handler) }
}
